import React, { useRef, useState } from 'react';
import { Spinner } from '../../components/ui';
import { Modal } from '../../components/ui';
import { sideChipClass, inviteChipClass, visibilityChipClass, fullName, nameInput, tenDigits, MAX_NAME, INVITE_STATUSES } from '../../utils/helpers';
import ExportButton from '../../components/ExportButton';
import MemberForm from './MemberForm';
import QuickAdd from './QuickAdd';
import CountGroupForm from './CountGroupForm';
import ContactImport from './ContactImport';
import FamilyMemberForm from './FamilyMemberForm';
import JoinFamily from './JoinFamily';

// Add a relative without leaving the family card: name (+ optional number),
// tap +, the member is saved and the box clears for the next one. The full
// form (phone book, existing guest, remarks) is still one tap away.
function QuickAddMember({ head, knownNames, onAdd }) {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const add = async () => {
    const guestName = name.trim();
    if (!guestName || busy) return;
    setBusy(true);
    try {
      await onAdd({ GuestName: guestName, Surname: head.Surname || null, MobileNumber: mobile || null });
      setName(''); setMobile('');
      ref.current?.focus();
    } finally { setBusy(false); }
  };

  return (
    <div className="quick-add-member">
      <input ref={ref} value={name} placeholder="Add member name" list="quick-known-names" maxLength={MAX_NAME}
        enterKeyHint="done" onChange={e => setName(nameInput(e.target.value))}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
      <input value={mobile} placeholder="Mobile (optional)" type="tel" inputMode="numeric" maxLength={10}
        onChange={e => setMobile(tenDigits(e.target.value))} />
      <button type="button" className="btn primary" disabled={!name.trim() || busy} onClick={add}>
        {busy ? '…' : '+'}
      </button>
      <datalist id="quick-known-names">{knownNames.map(s => <option key={s} value={s} />)}</datalist>
    </div>
  );
}

export default function MembersMobile({ vm }) {
  const {
    isAdmin, canAdd, canEdit, canDelete, mine, functions, invitableFunctions, rows, loading, stats,
    q, setQ, fnFilter, setFnFilter, fnName, adderCount, openAdd, openEdit, mark, del, open,
    canBulkImport, openBulk, bulkOpen, inviteRow, inviteSharing, inviteCooldown, openInvite, closeInvite, sendInvite, guestHasAllFunctions,
    addOrMoveFamilyMember, isMarriage, sideOptions, sort, setSort, SORT_OPTIONS,
    visibleCards, guestTypeFilter, setGuestTypeFilter, actionCooldown, beginFamilyAction, callGuest,
    familyOptions, knownNames, report, adderOptions, adderFilter, setAdderFilter, adder, groupOptions, groupFilter, setGroupFilter,
    singleFunction, partyLine, setVisibilityFor, savingVis, isPicked, togglePick, clearPicked, pickedFrom, addedByTargets, setAddedByFor, savingOwner,
  } = vm;
  // Pure view state: which card is expanded, which family form is open.
  const [expandedFamilyId, setExpandedFamilyId] = useState(null);
  const [expandedGuestId, setExpandedGuestId] = useState(null);
  const [familyFormHead, setFamilyFormHead] = useState(null);
  // Function picker is shown only when no function is pinned, or while switching.
  const [pickFn, setPickFn] = useState(false);
  // Sort / type filters live in a sheet: on a phone the list needs the screen,
  // and those two are set once in a while, not per guest.
  const [filtersOpen, setFiltersOpen] = useState(false);
  // The three "add" buttons live in one floating action within thumb reach.
  const [addOpen, setAddOpen] = useState(false);
  // The guest being put into an existing family.
  const [joinGuest, setJoinGuest] = useState(null);
  // "Select" turns the cards into tick boxes: pick a few, then make them
  // Private / Public or hand them to another member. Off by default - a phone
  // list is read far more often than it is edited in bulk.
  const [pickMode, setPickMode] = useState(false);
  const pickableRows = visibleCards.flatMap(c => (c.type === 'guest' ? [c.row] : [c.head, ...c.members])).filter(r => mine(r));
  const pickedShown = pickedFrom(pickableRows);
  const leavePickMode = () => { clearPicked(); setPickMode(false); };
  const filtersOn = guestTypeFilter !== 'all' || sort.key !== 'name' || sort.dir !== 'asc' || !!adderFilter || !!groupFilter;
  const cooldown = actionCooldown;

  const GuestDetails = ({ row, familyMember = false }) => {
    const editable = mine(row);
    const count = adderCount(row);
    return (
      <>
        <div className="mobile-card-head">
          <div style={{ minWidth: 0 }}>
            <strong className="mobile-title">{fullName(row)}</strong>
            {vm.isCountGroup(row) && <span className="chip count-group">👥 {Number(row.ExpectedCount || 1)}</span>}
            <div className="muted" style={{ fontSize: 12 }}>
              Added by {(row.AddedByName || row.InvitedByName || '').trim() || '-'} {Number(row.AddedByGuest) === 1 && <span className="chip guest-added" title="Added by an invited guest">Guest</span>}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <span className={sideChipClass(row.Side)}>{row.Side}</span>
        </div>

        <div className="mobile-meta">
          <div>Mobile: {row.MobileNumber || '-'}</div>
          <div>
            {row.FunctionNames || row.FunctionName || fnName(row.FunctionId)}
            <span className={visibilityChipClass(row.Visibility)} style={{ marginLeft: 6 }}>{row.Visibility || 'Private'}</span>
          </div>
          {isAdmin && (
            <div className="muted">
              Invited by {(row.AddedByName || row.InvitedByName || '').trim() || '-'} {Number(row.AddedByGuest) === 1 && <span className="chip guest-added" title="Added by an invited guest">Guest</span>}
              {count > 1 && <span className="chip warn" style={{ marginLeft: 6 }}>{count} members</span>}
            </div>
          )}
        </div>

        <div className="mobile-actions family-member-actions" style={{ alignItems: 'center' }}>
          {editable && canEdit ? (
            <select value={row.InviteStatus || 'Pending'} disabled={familyMember && cooldown > 0}
              onChange={e => familyMember
                ? beginFamilyAction(() => mark(row, e.target.value))
                : mark(row, e.target.value)}
              className={inviteChipClass(row.InviteStatus)}
              style={{ border: 'none', borderRadius: 999, padding: '5px 10px', fontSize: 12, fontWeight: 700 }}>
              {INVITE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <span className={inviteChipClass(row.InviteStatus)}>{row.InviteStatus}</span>
          )}
          {familyMember && row.MobileNumber && (
            <button className="btn light sm" disabled={cooldown > 0}
              onClick={() => beginFamilyAction(() => callGuest(row))}>Call</button>
          )}
          {editable && canEdit && (
            <button className="btn light sm" disabled={familyMember && cooldown > 0}
              onClick={() => familyMember
                ? beginFamilyAction(() => openEdit(row))
                : openEdit(row)}>Edit</button>
          )}
          {row.MobileNumber && (
            <button className="btn primary sm" onClick={() => openInvite(row)}>
              Invite on WhatsApp
            </button>
          )}
          {editable && canDelete && (
            <button className="btn danger sm" disabled={familyMember && cooldown > 0}
              onClick={() => familyMember
                ? beginFamilyAction(() => del(row))
                : del(row)}>Del</button>
          )}
          {/* A guest on their own can either BE a family (head) or JOIN one. */}
          {!familyMember && canAdd && editable && Number(row.FamilyId || 0) === 0 && (
            <>
              <button className="btn light sm" disabled={cooldown > 0}
                onClick={() => beginFamilyAction(() => setFamilyFormHead(row))}>Create Family</button>
              {familyOptions.length > 0 && (
                <button className="btn light sm" disabled={cooldown > 0}
                  onClick={() => beginFamilyAction(() => setJoinGuest(row))}>Join Family</button>
              )}
            </>
          )}
        </div>
      </>
    );
  };

  const CompactSummary = ({ row, family = false, memberCount, expanded, onClick }) => (
    <button type="button" className="guest-card-summary" onClick={onClick}>
      <div className="guest-card-main">
        <strong className="guest-card-name">
          {fullName(row)} {family && <span className="family-label">(Family)</span>}
        </strong>
        <div className="guest-card-sub">
          {row.MobileNumber || 'No phone'}
          {family ? ` · ${memberCount} members` : ''}
        </div>
        <div className="guest-card-functions">
          {(() => {
            // Three chips is all one phone row holds; the rest becomes "+n".
            const names = String(row.FunctionNames || row.FunctionName || fnName(row.FunctionId))
              .split(',').map(value => value.trim()).filter(Boolean);
            return (
              <>
                {names.slice(0, 3).map((functionName, index) => (
                  <span key={`${functionName}-${index}`} className={`function-chip function-chip-${index % 5}`}>
                    {functionName}
                  </span>
                ))}
                {names.length > 3 && <span className="function-chip function-chip-3">+{names.length - 3}</span>}
              </>
            );
          })()}
        </div>
      </div>
      <span className={sideChipClass(row.Side)}>
        {row.Side === 'Bride' || row.Side === 'Female' ? 'B'
          : row.Side === 'Groom' || row.Side === 'Male' ? 'G' : 'C'}
      </span>
      <span className="family-chevron">{expanded ? '▲' : '▼'}</span>
    </button>
  );

  return (
    <div className="guests-page">
      <div className="mobile-toolbar guest-toolbar-compact guest-controls-sticky">
        {/* A chosen function stays pinned at the top of the sticky bar while the
            list scrolls, so it is always clear which function you are working on.
            Side has no picker: it is fixed per user (admins use the dashboard toggle). */}
        {/* The pinned party says WHICH party and WHEN - a phone screen shown to
            someone else must not read as the whole event. */}
        {singleFunction && !pickFn && (
          <div className="guest-party-line m"><strong>Party:</strong> {partyLine}</div>
        )}
        {fnFilter && !pickFn && (
          <div className="pinned-function">
            {/* The pill IS the function control: tap the name to switch, x to show
                all. No second dropdown repeating the same name below. */}
            <button type="button" className="pinned-function-name" title="Change function" onClick={() => setPickFn(true)}>
              {fnName(fnFilter)} ({vm.fnCount(fnFilter)}) <span aria-hidden="true">▾</span>
            </button>
            <button type="button" className="pinned-function-clear" aria-label="Show all functions"
              onClick={() => setFnFilter('')}>&times;</button>
          </div>
        )}
        {/* ONE line of controls: the function (when none is pinned), the search
            box and the filter sheet. Everything else moved off the screen so
            the guests start at the top. */}
        <div className="guest-filter-row">
          {(!fnFilter || pickFn) && (
            <select value={fnFilter} autoFocus={pickFn} aria-label="Function filter"
              onChange={e => { setFnFilter(e.target.value); setPickFn(false); }}
              onBlur={() => setPickFn(false)}>
              <option value="">All functions</option>
              {functions.map(f => <option key={f.FunctionId} value={f.FunctionId}>{f.FunctionName} ({vm.fnCount(f.FunctionId)})</option>)}
            </select>
          )}
          <input placeholder="Search guests..." value={q} onChange={e => setQ(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
          <button type="button" className={'btn sm ' + (filtersOn ? 'primary' : 'light')} aria-label="Filters and sorting"
            onClick={() => setFiltersOpen(true)}>⚙</button>
          <ExportButton report={report} className="btn light sm" label="⇪" />
          {canEdit && (
            <button type="button" className={'btn sm ' + (pickMode ? 'primary' : 'light')} title="Pick several guests"
              onClick={() => (pickMode ? leavePickMode() : setPickMode(true))}>☑</button>
          )}
        </div>
        <div className="guest-stats-line">
          <strong>{visibleCards.length}</strong> shown · <strong>{stats.persons}</strong> persons
          {isMarriage ? <> · {stats.bride} bride · {stats.groom} groom</> : null}
        </div>
      </div>

      {adder && (
        <div className="muted added-by-line">
          <strong>Added by:</strong> {adder.label}
          <button type="button" className="linklike" onClick={() => setAdderFilter('')}>everyone</button>
        </div>
      )}

      {functions.length > 0 && !invitableFunctions.length && (
        <div className="muted" style={{ marginBottom: 10, fontSize: 13 }}>
          All functions of this event are over — guests can no longer be added.
        </div>
      )}
      {!functions.length && (
        <div className="muted" style={{ marginBottom: 10, fontSize: 13 }}>
          No functions yet. An Event Admin must add functions before guests can be invited.
        </div>
      )}

      {pickMode && (
        <div className="guest-bulk-bar m">
          <strong>{pickedShown.length} picked</strong>
          <div className="guest-bulk-actions">
            <button type="button" className="btn light sm" disabled={!pickedShown.length || savingVis}
              onClick={() => setVisibilityFor(pickedShown, false)}>🔒 Private</button>
            <button type="button" className="btn light sm" disabled={!pickedShown.length || savingVis}
              onClick={() => setVisibilityFor(pickedShown, true)}>👁 Public</button>
            <button type="button" className="linklike" onClick={leavePickMode}>Done</button>
          </div>
          {isAdmin && (
            <select value="" disabled={!pickedShown.length || savingOwner} aria-label="Hand these guests to"
              onChange={(e) => {
                const to = addedByTargets.find(u => String(u.value) === e.target.value);
                if (!to) return;
                if (!window.confirm(`Hand ${pickedShown.length} ${pickedShown.length === 1 ? 'guest' : 'guests'} to ${to.label}?`)) return;
                setAddedByFor(pickedShown, to.value);
              }}>
              <option value="">Added by → choose a member…</option>
              {addedByTargets.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          )}
        </div>
      )}

      {loading ? <div className="card card-pad"><Spinner /></div>
        : visibleCards.length === 0 ? (
          rows.length === 0 ? (
            <div className="card card-pad empty" style={{ padding: '28px 16px' }}>
              <div style={{ fontSize: 34 }}>📇</div>
              <div style={{ fontWeight: 700, color: 'var(--ink)', marginTop: 6 }}>No guests in your list yet</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                {canBulkImport
                  ? 'Pick family and friends straight from your phone contacts — tick many at once.'
                  : 'Add guests one by one, or open the mobile app to pick them from your phone contacts.'}
              </div>
              {canAdd && canBulkImport && (
                <button className="btn primary" onClick={openBulk} disabled={!invitableFunctions.length} style={{ marginTop: 14, padding: '12px 20px' }}>
                  📇 Add from Contacts
                </button>
              )}
            </div>
          ) : <div className="card card-pad empty">No matching guests.</div>
        ) : (
          <div className="mobile-card-list">
            {visibleCards.map(card => {
              if (card.type === 'guest') {
                const expanded = String(expandedGuestId) === String(card.row.GuestId);
                return (
                  <div key={card.key} className={`card mobile-record-card compact-guest-card ${expanded ? 'expanded' : ''}${isPicked(card.row) ? ' g-picked' : ''}`}>
                    <div className="guest-card-pickline">
                      {pickMode && (
                        <input type="checkbox" checked={isPicked(card.row)} disabled={!mine(card.row)}
                          aria-label="Pick this guest" onChange={() => togglePick(card.row)} />
                      )}
                      <CompactSummary
                        row={card.row}
                        expanded={expanded}
                        onClick={() => (pickMode && mine(card.row) ? togglePick(card.row) : setExpandedGuestId(expanded ? null : card.row.GuestId))}
                      />
                    </div>
                    {expanded && (
                      <div className="guest-card-expanded">
                        <GuestDetails row={card.row} />
                      </div>
                    )}
                  </div>
                );
              }
              const expanded = String(expandedFamilyId) === String(card.familyId);
              const head = card.head;
              return (
                <div key={card.key} className={`card mobile-record-card family-card ${expanded ? 'expanded' : ''}${isPicked(head) ? ' g-picked' : ''}`}>
                  <div className="guest-card-pickline">
                    {pickMode && (
                      <input type="checkbox" checked={isPicked(head)} disabled={!mine(head)}
                        aria-label="Pick this family" title="The whole family is picked"
                        onChange={() => { togglePick(head); card.members.filter(m => mine(m)).forEach(m => togglePick(m)); }} />
                    )}
                    <CompactSummary
                      row={head}
                      family
                      memberCount={card.members.length}
                      expanded={expanded}
                      onClick={() => setExpandedFamilyId(expanded ? null : card.familyId)}
                    />
                  </div>
                  {expanded && (
                    <div className="family-members">
                      {canAdd && mine(head) && (
                        <>
                          <QuickAddMember
                            head={head}
                            knownNames={knownNames}
                            onAdd={(member) => addOrMoveFamilyMember(head, member)}
                          />
                          <button type="button" className="btn light family-add-member" disabled={cooldown > 0}
                            onClick={() => beginFamilyAction(() => setFamilyFormHead(head))}>
                            {cooldown > 0 ? <><span className="cooldown-ring">{cooldown}</span> Wait</> : '📇 Phone book / existing guest'}
                          </button>
                        </>
                      )}
                      {card.members.map((member, index) => (
                        <div key={member.GuestId} className="family-member">
                          <div className="family-member-number">{index + 1}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <GuestDetails row={member} familyMember />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      {/* Add is a floating action: reachable with the thumb while scrolling. */}
      {canAdd && !!invitableFunctions.length && (
        <>
          {addOpen && <button type="button" className="fab-backdrop" aria-label="Close" onClick={() => setAddOpen(false)} />}
          <div className="fab-wrap">
            {addOpen && (
              <div className="fab-menu">
                <button type="button" className="btn light" onClick={() => { setAddOpen(false); openAdd('individual'); }}>👤 Guest</button>
                <button type="button" className="btn light" onClick={() => { setAddOpen(false); openAdd('family'); }}>👪 Family</button>
                <button type="button" className="btn light" onClick={() => { setAddOpen(false); vm.quick.openQuick(); }}>⚡ Quick list</button>
                <button type="button" className="btn light" onClick={() => { setAddOpen(false); vm.countGroup.openGroupAdd(); }}>👥 Group (count only)</button>
                {canBulkImport && (
                  <button type="button" className="btn light" onClick={() => { setAddOpen(false); openBulk(); }}>📇 Contacts</button>
                )}
              </div>
            )}
            <button type="button" className={'fab' + (addOpen ? ' open' : '')} aria-label="Add guests"
              onClick={() => setAddOpen(o => !o)}>{addOpen ? '×' : '+'}</button>
          </div>
        </>
      )}

      {vm.quick.quickOpen && <QuickAdd vm={vm} />}
      {vm.countGroup.groupOpen && <CountGroupForm vm={vm} />}

      {filtersOpen && (
        <Modal className="gx-m-filters" title="Filters & sorting" onClose={() => setFiltersOpen(false)}
          footer={<>
            <button className="btn light" style={{ padding: 12 }} onClick={() => { setGuestTypeFilter('all'); setGroupFilter(''); setSort({ key: 'name', dir: 'asc' }); }}>Reset</button>
            <button className="btn primary" style={{ flex: 1, padding: 12 }} onClick={() => setFiltersOpen(false)}>Done</button>
          </>}>
          <div className="step-label">Show</div>
          <div className="gx-m-seg grid2" style={{ marginBottom: 14 }}>
            {[['all', 'Everyone'], ['family', 'Families'], ['individual', 'Individuals'], ['business', '🏢 Business'], ['home', '🏠 Own family']].map(([value, label]) => (
              <button key={value} type="button" className={guestTypeFilter === value ? 'on' : ''}
                onClick={() => setGuestTypeFilter(value)}>{label}</button>
            ))}
          </div>
          {adderOptions.length > 1 && (
            <>
              <div className="step-label">Added by</div>
              <div className="chips-row" style={{ marginBottom: 14 }}>
                <button type="button" className={'chip-toggle' + (adderFilter ? '' : ' on')}
                  onClick={() => setAdderFilter('')}>Everyone</button>
                {adderOptions.map(u => (
                  <button key={u.value} type="button" className={'chip-toggle' + (String(adderFilter) === u.value ? ' on' : '')}
                    onClick={() => setAdderFilter(u.value)}>{u.name} ({u.count})</button>
                ))}
              </div>
            </>
          )}
          <div className="step-label">Group</div>
          <div className="chips-row" style={{ marginBottom: 14 }}>
            <button type="button" className={'chip-toggle' + (groupFilter ? '' : ' on')}
              onClick={() => setGroupFilter('')}>All</button>
            {groupOptions.list.filter(o => o.count > 0 || groupFilter === o.value).map(o => (
              <button key={o.value} type="button" className={'chip-toggle' + (groupFilter === o.value ? ' on' : '')}
                onClick={() => setGroupFilter(o.value)}>{o.value} ({o.count})</button>
            ))}
            {groupOptions.none > 0 && (
              <button type="button" className={'chip-toggle' + (groupFilter === '__none' ? ' on' : '')}
                onClick={() => setGroupFilter('__none')}>No group ({groupOptions.none})</button>
            )}
          </div>
          <div className="step-label">Sort by</div>
          <div className="chips-row">
            {SORT_OPTIONS.map(([k, l]) => (
              <button key={k} type="button" className={'chip-toggle' + (sort.key === k ? ' on' : '')}
                onClick={() => setSort(x => ({ key: k, dir: x.dir }))}>{l}</button>
            ))}
          </div>
          <div className="gx-m-seg" style={{ marginTop: 10, marginBottom: 0 }}>
            <button type="button" className={sort.dir === 'asc' ? 'on' : ''} onClick={() => setSort(x => ({ ...x, dir: 'asc' }))}>A→Z · low→high</button>
            <button type="button" className={sort.dir === 'desc' ? 'on' : ''} onClick={() => setSort(x => ({ ...x, dir: 'desc' }))}>Z→A · high→low</button>
          </div>
        </Modal>
      )}

      {joinGuest && (
        <JoinFamily
          guest={joinGuest}
          families={familyOptions}
          onClose={() => setJoinGuest(null)}
          onPick={async (fam) => {
            const guest = joinGuest;
            setJoinGuest(null);
            await addOrMoveFamilyMember(fam.head, { ExistingGuestId: guest.GuestId });
            setExpandedFamilyId(fam.familyId);
          }}
        />
      )}

      {open && <MemberForm vm={vm} />}
      {familyFormHead && (
        <FamilyMemberForm
          head={familyFormHead}
          rows={rows}
          functions={functions}
          isMarriage={isMarriage}
          sideOptions={sideOptions}
          onClose={() => setFamilyFormHead(null)}
          onSave={async member => {
            await addOrMoveFamilyMember(familyFormHead, member);
            setExpandedFamilyId(familyFormHead.GuestId);
          }}
        />
      )}
      {inviteRow && (
        <Modal className="gx-invite-modal" title="Invite on WhatsApp" onClose={closeInvite} footer={<button className="btn light" style={{ padding: 12 }} onClick={closeInvite} disabled={!!inviteSharing}>Cancel</button>}>
          <div className="gx-invite-list">
            {inviteCooldown > 0 && (
              <div className="invite-cooldown-note">
                Please wait {inviteCooldown} seconds before sending another invitation.
              </div>
            )}
            <button className="btn primary" disabled={!!inviteSharing || inviteCooldown > 0} onClick={() => sendInvite('image')}>
              {inviteSharing === 'image' ? 'Opening...' : 'Image Invite'}
            </button>
            <button className="btn light" disabled={!!inviteSharing || inviteCooldown > 0} onClick={() => sendInvite('text')}>
              {inviteSharing === 'text' ? 'Opening...' : 'Text Invite'}
            </button>
            <button className="btn light" disabled={!!inviteSharing || inviteCooldown > 0 || !guestHasAllFunctions(inviteRow)} onClick={() => sendInvite('pdf')}>
              {inviteSharing === 'pdf' ? 'Opening...' : 'PDF Invite'}
            </button>
            {!guestHasAllFunctions(inviteRow) && (
              <div className="muted" style={{ fontSize: 12 }}>PDF invite is available only when this guest is invited to all functions.</div>
            )}
          </div>
        </Modal>
      )}
      {bulkOpen && <ContactImport vm={vm} />}
    </div>
  );
}
